import { IApiBaseProps } from '@common/api.models';
import { CheckboxGroup, Label, CheckboxType, IOption } from '@gcl/react-core';
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDeepCompareEffectNoCheck } from 'use-deep-compare-effect';
import { OptionFilter, IFilterInputBaseProps, OptionsFilter, IOptionFilter } from '../searchFilter.models';
import { getFilterValue } from '../searchFilter.services';

export interface ICheckboxGroupFilterInputProps extends IFilterInputBaseProps<OptionFilter | OptionsFilter> {}

export const CheckboxGroupFilterInput: React.FunctionComponent<ICheckboxGroupFilterInputProps> = ({
  fieldConfig,
  onChange,
  filter = {},
  disabled = false,
}) => {
  const [localOptions, setLocalOptions] = React.useState<OptionsFilter>(undefined);
  const [localValue, setLocalValue] = React.useState<OptionsFilter>(undefined);
  const {
    fieldName,
    displayName,
    useFetchOptions,
    useFetchOptionsProps,
    optionPageSize,
    allowMultipleSelect,
    disable,
  } = fieldConfig;

  useDeepCompareEffectNoCheck(() => {
    const value = getFilterValue(fieldName, filter);

    if (!value) {
      setLocalValue(undefined);
      return;
    }

    if (!allowMultipleSelect) {
      const optionFilter = value as IOptionFilter;
      setLocalValue([optionFilter]);
      return;
    }

    const optionsFilter = value as IOptionFilter[];
    setLocalValue(optionsFilter);
  }, [JSON.stringify(filter)]);

  const useFetchOptionsPlaceHolder = (apiBaseProps: IApiBaseProps) => {
    return useQuery([apiBaseProps.projectId, 'useFetchOptionsPlaceHolder'], async () => {
      return [] as IOption<any>[];
    });
  };

  const { data, isLoading } = (useFetchOptions || useFetchOptionsPlaceHolder)(useFetchOptionsProps);

  useDeepCompareEffectNoCheck(() => {
    if (!data) {
      setLocalOptions(undefined);
      return;
    }

    setLocalOptions(
      data.map((option) => {
        return { id: option.key, value: option.text } as IOptionFilter;
      }),
    );
  }, [JSON.stringify(data)]);

  return (
    <>
      <Label labelFor={fieldName}>{displayName}</Label>
      <CheckboxGroup
        value={localValue}
        disabled={disabled || disable}
        displayFieldName="value"
        id={fieldName}
        name={fieldName}
        type={allowMultipleSelect ? CheckboxType.Checkbox : CheckboxType.Radio}
        checkOptions={localOptions || []}
        className="gcl-gems-checkbox-group-filter-input"
        itemPerRow={2}
        allowSelectAll
        onChange={(newValue) => {
          if (!newValue || newValue.length <= 0) {
            setLocalValue(undefined);
            onChange(fieldName, undefined);
            return;
          }

          setLocalValue(newValue);
          if (!allowMultipleSelect) {
            onChange(fieldName, newValue[0]);
            return;
          }
          onChange(fieldName, newValue);
        }}
      />
    </>
  );
};
